import { SupabaseClient } from "@supabase/supabase-js";

const PENDING_ORG_NAME_KEY = "pending_org_name";

export function getPendingOrgName(): string | null {
    return localStorage.getItem(PENDING_ORG_NAME_KEY);
}

export function setPendingOrgName(name: string) {
    if (name && name.trim().length > 0) {
        localStorage.setItem(PENDING_ORG_NAME_KEY, name.trim());
    }
}

export function clearPendingOrgName() {
    localStorage.removeItem(PENDING_ORG_NAME_KEY);
}

/**
 * Ensures the user has an organization set up.
 * 1. Checks for existing membership.
 * 2. If missing, attempts to create org using name from localStorage or user_metadata.
 * 
 * Returns { success: boolean, message?: string }
 */
export async function ensureOrgSetup(supabase: SupabaseClient, userId: string): Promise<{ success: boolean; message?: string }> {
    try {
        // 1. Check existing membership
        const { data: membership, error: memErr } = await supabase
            .from("organization_members")
            .select("organization_id")
            .eq("user_id", userId)
            .maybeSingle();

        if (memErr) {
            console.error("Org membership check failed:", memErr);
            return { success: false, message: "Could not verify organization access." };
        }

        if (membership) {
            // User has an org. Check if they have the legacy 'admin' role required by dashboard
            const { data: role } = await supabase
                .from("user_roles")
                .select("role")
                .eq("user_id", userId)
                .eq("role", "admin")
                .maybeSingle();

            if (role) {
                return { success: true };
            }

            // Has org but no legacy role -> Call create-organization to backfill it.
            // We pass a dummy name because it won't be used if membership exists.

            await supabase.functions.invoke('create-organization', {
                body: { name: "Organization Check" }
            });

            return { success: true };
        }

        // 2. No membership -> Try to create org
        let orgName = getPendingOrgName();

        // Fallback to user metadata if localStorage is empty
        if (!orgName) {
            const { data: { user } } = await supabase.auth.getUser();
            const metaName = user?.user_metadata?.company_name;
            if (metaName && typeof metaName === 'string') {
                orgName = metaName;
            }
        }

        if (!orgName) {
            // No name found anywhere -> cannot auto-create
            return { success: false };
        }

        // 3. Create org with found name
        const { error: createErr } = await supabase.functions.invoke('create-organization', {
            body: { name: orgName }
        });

        if (createErr) {
            console.error("Auto-create org failed:", createErr);
            // Return false so user sees the retry UI with the name pre-filled (if we pass it back? 
            // actually UI will pull from local/meta same as we did).
            return { success: false, message: "Organization setup failed. Please retry." };
        }

        // Success -> clear pending
        clearPendingOrgName();
        return { success: true };

    } catch (err) {
        console.error("ensureOrgSetup error:", err);
        return { success: false, message: "An unexpected error occurred during setup." };
    }
}
